import { create } from 'zustand';
import type { WorkspaceFile, Tab } from '@/types';

function languageFromPath(path: string): string {
  const ext = path.split('.').pop()?.toLowerCase() ?? '';
  const map: Record<string, string> = {
    ts: 'typescript',
    tsx: 'typescriptreact',
    js: 'javascript',
    jsx: 'javascriptreact',
    json: 'json',
    md: 'markdown',
    sol: 'sol',
    css: 'css',
    html: 'html',
    yaml: 'yaml',
    yml: 'yaml',
    toml: 'toml',
    py: 'python',
    rs: 'rust',
    go: 'go',
    sh: 'shell',
    txt: 'plaintext',
  };
  return map[ext] ?? 'plaintext';
}

function fileNameFromPath(path: string): string {
  return path.split('/').pop() ?? path;
}

export interface InsertRequest {
  text: string;
  id: string;
}

interface EditorState {
  fileTree: WorkspaceFile | null;
  tabs: Tab[];
  activeTabId: string | null;
  openFiles: Map<string, string>;
  isModified: Map<string, boolean>;
  /** Pending text-insert request from an extension; consumed by the active CodeEditor. */
  insertRequest: InsertRequest | null;

  setFileTree: (tree: WorkspaceFile | null) => void;
  openFile: (path: string, content: string, language?: string) => void;
  closeTab: (id: string) => void;
  setActiveTab: (id: string) => void;
  updateFileContent: (path: string, content: string) => void;
  saveFile: (path: string) => void;
  closeAllTabs: () => void;
  closeOtherTabs: (id: string) => void;
  requestInsert: (text: string) => void;
  clearInsertRequest: () => void;
}

export const useEditorStore = create<EditorState>((set, get) => ({
  fileTree: null,
  tabs: [],
  activeTabId: null,
  openFiles: new Map(),
  isModified: new Map(),
  insertRequest: null,

  setFileTree: (tree) => set({ fileTree: tree }),

  openFile: (path, content, language) => {
    const state = get();
    const existing = state.tabs.find((t) => t.path === path);
    if (existing) {
      set({
        tabs: state.tabs.map((t) => ({ ...t, isActive: t.id === existing.id })),
        activeTabId: existing.id,
      });
      return;
    }

    const id = crypto.randomUUID();
    const lang = language ?? languageFromPath(path);
    const name = fileNameFromPath(path);

    const newTab: Tab = {
      id,
      path,
      name,
      language: lang,
      isModified: false,
      isActive: true,
    };

    const newOpenFiles = new Map(state.openFiles);
    newOpenFiles.set(path, content);

    set({
      tabs: [...state.tabs.map((t) => ({ ...t, isActive: false })), newTab],
      activeTabId: id,
      openFiles: newOpenFiles,
    });
  },

  closeTab: (id) => {
    const state = get();
    const tab = state.tabs.find((t) => t.id === id);
    if (!tab) return;

    const remaining = state.tabs.filter((t) => t.id !== id);
    const newOpenFiles = new Map(state.openFiles);
    const newIsModified = new Map(state.isModified);

    // Only remove content if no other tab references this path
    if (!remaining.some((t) => t.path === tab.path)) {
      newOpenFiles.delete(tab.path);
      newIsModified.delete(tab.path);
    }

    let activeTabId = state.activeTabId;
    if (activeTabId === id) {
      const idx = state.tabs.findIndex((t) => t.id === id);
      const next = remaining[Math.min(idx, remaining.length - 1)] ?? null;
      activeTabId = next?.id ?? null;
      remaining.forEach((t) => (t.isActive = t.id === activeTabId));
    }

    set({ tabs: remaining, activeTabId, openFiles: newOpenFiles, isModified: newIsModified });
  },

  setActiveTab: (id) => {
    set((s) => ({
      tabs: s.tabs.map((t) => ({ ...t, isActive: t.id === id })),
      activeTabId: id,
    }));
  },

  updateFileContent: (path, content) => {
    const state = get();
    const newOpenFiles = new Map(state.openFiles);
    newOpenFiles.set(path, content);
    const newIsModified = new Map(state.isModified);
    newIsModified.set(path, true);

    set({
      openFiles: newOpenFiles,
      isModified: newIsModified,
      tabs: state.tabs.map((t) =>
        t.path === path ? { ...t, isModified: true } : t
      ),
    });
  },

  saveFile: (path) => {
    const state = get();
    const newIsModified = new Map(state.isModified);
    newIsModified.set(path, false);

    set({
      isModified: newIsModified,
      tabs: state.tabs.map((t) =>
        t.path === path ? { ...t, isModified: false } : t
      ),
    });
  },

  closeAllTabs: () => {
    set({
      tabs: [],
      activeTabId: null,
      openFiles: new Map(),
      isModified: new Map(),
    });
  },

  closeOtherTabs: (id) => {
    const state = get();
    const keep = state.tabs.find((t) => t.id === id);
    if (!keep) return;

    const newOpenFiles = new Map<string, string>();
    const newIsModified = new Map<string, boolean>();
    const content = state.openFiles.get(keep.path);
    if (content !== undefined) newOpenFiles.set(keep.path, content);
    const modified = state.isModified.get(keep.path);
    if (modified !== undefined) newIsModified.set(keep.path, modified);

    set({
      tabs: [{ ...keep, isActive: true }],
      activeTabId: id,
      openFiles: newOpenFiles,
      isModified: newIsModified,
    });
  },

  requestInsert: (text: string) => {
    set({ insertRequest: { text, id: crypto.randomUUID() } });
  },

  clearInsertRequest: () => {
    set({ insertRequest: null });
  },
}));
